import { cn } from "@/lib/utils";
import { useConsoleApi } from "./useConsoleApi";
import { SEVERITIES, type CaBootsResp } from "./types";
import { SEV_STYLE, STATUS_STYLE, fmtDateTime, fmtDuration, fmtNum, timeAgo } from "./utils";
import { EmptyState } from "./FindingsTab";


export default function BootsTab({ refreshMs, now, selectedId, onSelect }: {
    refreshMs: number;
    now: number;
    selectedId?: string;
    onSelect: (id: string) => void;
}) {
    const resp = useConsoleApi<CaBootsResp>('boots', {}, refreshMs);
    if (resp.error) return <EmptyState>Failed to load boots: {(resp.error as Error).message}</EmptyState>;
    if (!resp.data) return <EmptyState>Loading…</EmptyState>;
    if (!resp.data.boots.length) return <EmptyState>No boot recorded yet.</EmptyState>;

    return (
        <div className="flex flex-col gap-2">
            <span className="text-xs text-muted-foreground">
                The last 20 boots are kept. Click a boot to inspect its findings.
            </span>
            <div className="overflow-x-auto rounded-lg border bg-card">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b text-left text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                            <th className="px-3 py-2">Boot</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2">Started</th>
                            <th className="px-3 py-2 text-right">Boot time</th>
                            <th className="px-3 py-2 text-right">Lines</th>
                            <th className="px-3 py-2 text-right">Resources</th>
                            <th className="px-3 py-2 text-right">Failed</th>
                            {SEVERITIES.map((sev) => (
                                <th key={sev} className={cn('px-2 py-2 text-right', SEV_STYLE[sev].text)}>{sev.slice(0, 4)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {resp.data.boots.map((b) => (
                            <tr
                                key={b.id}
                                onClick={() => onSelect(b.id)}
                                className={cn(
                                    'cursor-pointer border-b last:border-b-0 hover:bg-muted/30',
                                    selectedId === b.id && 'bg-muted/50',
                                )}
                            >
                                <td className="px-3 py-1.5 font-mono text-xs">
                                    #{b.id}
                                    {b.isCurrent && <span className="ml-2 text-2xs uppercase tracking-wider text-muted-foreground">current</span>}
                                </td>
                                <td className={cn('px-3 py-1.5 text-xs font-semibold uppercase', STATUS_STYLE[b.status])}>{b.status}</td>
                                <td className="px-3 py-1.5 text-xs" title={fmtDateTime(b.startedAt)}>
                                    {fmtDateTime(b.startedAt)}
                                    <span className="ml-2 text-muted-foreground">{timeAgo(b.startedAt, now)}</span>
                                </td>
                                <td className="px-3 py-1.5 text-right font-mono text-xs">{fmtDuration(b.bootDurationMs)}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-xs">{fmtNum(b.lines)}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-xs">
                                    {b.resourcesStarted}{b.resourcesFound !== null ? ` / ${b.resourcesFound}` : ''}
                                </td>
                                <td className={cn('px-3 py-1.5 text-right font-mono text-xs', b.failedResources.length ? 'text-red-500' : 'text-muted-foreground/40')}>
                                    {b.failedResources.length || '·'}
                                </td>
                                {SEVERITIES.map((sev) => (
                                    <td key={sev} className={cn('px-2 py-1.5 text-right font-mono text-xs', b.counts[sev] ? SEV_STYLE[sev].text : 'text-muted-foreground/40')}>
                                        {b.counts[sev] || '·'}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
